/* eslint-disable @typescript-eslint/naming-convention */
import {baseApi} from 'core/redux/store/api';
import {IFilterFacetResponse} from 'plugins/modules/filter/types';
import {TGetProductsResponse} from 'modules/products/models';
import {IProductShort} from 'modules/products/types';
import {createListingApiService, TGetListingRequest} from 'plugins/modules/listing/api/api';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {getListHeadersWithCookies} from 'core/helpers/cookie';
import {IStock} from 'modules/stock-x/types';
import {API_VERSION} from 'core/helpers/env';

export interface IGetCatalogFiltersProps {
    filter?: string;
    section?: string;
    tabId?: string;
}

const API_PATHS = {
    STOCK_DETAIL: (code?: string) => `${API_VERSION}/stocks/${code}`,
    STOCK_DETAIL_PRODUCTS: (code?: string) => `${API_VERSION}/stocks/${code}/products`,
    STOCK_DETAIL_FACET_SEARCH: (code?: string) => `${API_VERSION}/stocks/${code}/facetsearch`,
} as const;

const appendTabIdParam = (params: URLSearchParams, tabId?: string) => {
    if (!tabId) {
        return;
    }

    params.set('filter[tabId]', tabId);
};

export const StockDetailApi = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getStockFilters: builder.query({
            providesTags: ['FacetFilters'],
            query: ({filter, section, tabId}: IGetCatalogFiltersProps) => {
                const baseUrl = API_PATHS.STOCK_DETAIL_FACET_SEARCH(section);
                const params = new URLSearchParams(filter || '');

                appendTabIdParam(params, tabId);

                const queryString = decodeURIComponent(params.toString());

                return queryString ? `${baseUrl}?${queryString}` : baseUrl;
            },
            transformResponse: (response: {data: IFilterFacetResponse}) => response?.data,
        }),
        getStock: builder.query({
            query: (stockCode: string) => {
                return API_PATHS.STOCK_DETAIL(stockCode);
            },
            transformResponse: (response: {data: IStock}) => response?.data,
        }),
    }),
});

const StockProductsListingApiService = createListingApiService<
    IProductShort,
    TGetProductsResponse,
    TGetListingTransformedResponse<IProductShort>,
    TGetListingRequest<{section: string; tabId?: string}>
>({
    name: 'getStockProducts',
    query: (query) => {
        const headers = getListHeadersWithCookies();
        const params = new URLSearchParams(query.filter || '');

        appendTabIdParam(params, query.tabId);
        params.set('pageNumber', String(query.pageNumber));
        params.set('pageSize', String(query.pageSize));
        params.set('withMeta', query.withMeta ? '1' : '0');

        if (query.sort) {
            params.set('sort', query.sort);
        }

        const queryString = decodeURIComponent(params.toString());

        return {
            url: `${API_PATHS.STOCK_DETAIL_PRODUCTS(query.section)}?${queryString}`,
            headers,
        };
    },
    serializeQueryArgs: ({endpointName, queryArgs}) => {
        return `${endpointName}_${queryArgs.section}${queryArgs.filter ? `_${queryArgs.filter}` : ''}_${queryArgs.sort}${queryArgs.tabId ? `_${queryArgs.tabId}` : ''}`;
    },
    forceRefetchValuesKeys: ['section', 'tabId'],
});

export const {getStockProducts} = StockProductsListingApiService.endpoints;
export const {useLazyGetStockProductsQuery} = StockProductsListingApiService;

export const {getStock} = StockDetailApi.endpoints;
export const {useGetStockQuery, useGetStockFiltersQuery} = StockDetailApi;
